/**
 * API helpers for the invitation-by-token flow.
 *
 * Public fetch (unauthenticated) and authenticated accept calls live here so the
 * screen stays focused on render logic + state.
 */

import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { buildEndpointUrl } from '../../../constants';

export interface InvitationInviter {
  id: string;
  firstName: string | null;
  lastName: string | null;
  avatar: string | null;
}

export interface Invitation {
  id: string;
  isShareLink: boolean;
  inviter: InvitationInviter;
  expiresAt: string;
  maxUses: number;
  usedCount: number;
}

interface InvitationFetchEnvelope {
  success: boolean;
  data?: Invitation;
  error?: string;
}

interface InvitationAcceptEnvelope {
  success: boolean;
  error?: string;
}

type AuthenticatedApiClient = <T>(
  endpoint: string,
  options?: AxiosRequestConfig,
) => Promise<AxiosResponse<T>>;

/**
 * Public read — the by-token endpoint does NOT require auth (used by un-signed-in
 * recipients clicking the email link). Uses plain axios so no Authorization
 * header is sent.
 */
export async function fetchInvitationByToken(token: string): Promise<Invitation> {
  const response = await axios.get<InvitationFetchEnvelope>(
    `${buildEndpointUrl('invitations', 'getByToken')}/${encodeURIComponent(token)}`,
  );
  if (!response.data?.success || !response.data.data) {
    throw new Error(response.data?.error ?? 'Invitation not found');
  }
  return response.data.data;
}

/**
 * Authenticated accept — uses the project's apiClient so the Supabase JWT is
 * attached automatically. Previously the screen attached `Bearer ${userId}`,
 * which is not a valid token; `useApiClient` is the canonical path.
 */
export async function acceptInvitationByToken(
  apiClient: AuthenticatedApiClient,
  token: string,
): Promise<void> {
  const response = await apiClient<InvitationAcceptEnvelope>(
    `${buildEndpointUrl('invitations', 'accept')}/${encodeURIComponent(token)}/accept`,
    { method: 'POST' },
  );
  if (!response.data?.success) {
    throw new Error(response.data?.error ?? 'Failed to accept invitation');
  }
}
